import {
  generatePath,
  Link,
  matchPath,
  useMatches,
  useParams,
} from "react-router-dom";
import { FlipBackward } from "@untitled-ui/icons-react";
import { ReactComponent as LogoCyStackLettermark } from "#src/assets/images/logos/logo-cystack-lettermark.svg";
import { ReactComponent as LogoCyStack } from "#src/assets/images/logos/logo-cystack.svg";
import { pathname } from "#src/config/pathname";
import {
  sidebarV2Items,
  sidebarV2SecondMenuItems,
  sidebarV2SettingItems,
  sidebarV2BottomHalfItems,
  collapsedMenuPages,
} from "./config";
import { HTMLAttributes, useMemo } from "react";
import { SidebarProductItemRender } from "./render/Product";
import { SidebarPageItemRender } from "./render/Page";
import { twMerge } from "tailwind-merge";
import { LayoutV2SidebarUserProfile } from "./UserProfile";
import { useTranslation } from "react-i18next";

export const LayoutV2Sidebar = ({
  openSidebar,
  className,
}: {
  openSidebar: boolean;
  className?: HTMLAttributes<HTMLDivElement>["className"];
}) => {
  useTranslation("layout", { keyPrefix: "sidebar.v2" });

  const pathMatches = useMatches();

  const { workspaceId } = useParams<"workspaceId">();

  // --------------- EFFECTS ---------------

  // Return Second Menu object if current path is matched and Second Menu should be shown, return undefined otherwise and show the Main Menu.
  const secondMenu = useMemo(() => {
    return sidebarV2SecondMenuItems.find((item) =>
      pathMatches.some((match) => matchPath(item.rootPath, match.pathname))
    );
  }, [pathMatches]);

  // If current path is on the list of pages that should have a collapsed menu, return true.
  const isOnCollapsedMenu = useMemo(
    () =>
      collapsedMenuPages.some((item) =>
        pathMatches.some((match) => matchPath(item, match.pathname))
      ),
    [pathMatches]
  );

  const firstMenuShouldCollapse = useMemo(() => {
    return isOnCollapsedMenu || Boolean(secondMenu);
  }, [isOnCollapsedMenu, secondMenu]);

  const isOnSettingPage = useMemo(() => {
    return sidebarV2SettingItems
      .map((item) => item.children)
      .flat()
      .some((item) =>
        pathMatches.some((match) => matchPath(item, match.pathname))
      );
  }, [pathMatches]);

  // --------------- RENDER ---------------

  if (!workspaceId) return null;

  return (
    <div
      // className of this component is casted outside so that it can be controlled where it's easier to contextualize.
      className={twMerge(
        className,
        "transition-all ease-in-out",
        openSidebar
          ? isOnCollapsedMenu
            ? "w-[3.75rem]"
            : "w-[18.5rem]"
          : "w-0"
      )}
    >
      <div
        className={`border-r ${firstMenuShouldCollapse ? "w-[3.75rem] border-gray-v2-200" : "w-full border-transparent"} h-screen flex flex-col justify-between`}
      >
        {/* ---------- Upper half ---------- */}
        {/* Upper half should contain navigating items */}
        <div className="pt-6 flex flex-col gap-4 min-h-0">
          {/* Trademark as home button */}
          <Link
            to={generatePath(pathname.SOLUTIONS, { workspaceId })}
            className="px-4 flex"
          >
            {/* Change logo when this sidebar level is collapsed/expanded. */}
            <LogoCyStackLettermark
              className={`fill-black h-7 ${firstMenuShouldCollapse ? "appearance-none opacity-10 w-0" : ""}`}
            />
            <LogoCyStack
              className={`fill-black h-7 ${firstMenuShouldCollapse ? "w-7" : "appearance-none opacity-10 w-0"}`}
            />
          </Link>
          {/* Trademark as home button end */}

          {/* Main Menu, which should contain most of our product */}
          <div
            style={{ scrollbarWidth: "none" }}
            // If the span label below is enabled, this div should have gap-4
            className={`${firstMenuShouldCollapse ? "px-2" : "px-4"} overflow-x-auto`}
          >
            {sidebarV2Items.map((group) => (
              <div key={`sidebar-group-${group.key}`} className="flex flex-col">
                {/* This should be shown but the clowns writing the specs just can't decide themselves. */}
                {/* <span className="px-3 py-1 text-xs font-semibold text-gray-v2-400">
                  {group.getLabel()}
                </span> */}
                {group.products.map((product, productIndex) => {
                  if (product === "SEPARATOR") {
                    return (
                      <div
                        key={`sidebar-product-separator-${productIndex}`}
                        className="my-2 border-t border-gray-v2-200"
                      />
                    );
                  }

                  return (
                    <SidebarProductItemRender
                      key={`sidebar-item-product-${product.path}`}
                      product={product}
                      workspaceId={workspaceId}
                      pathMatches={pathMatches}
                      minified={firstMenuShouldCollapse}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          {/* Main menu end */}
        </div>
        {/* ---------- Upper half end ---------- */}

        {/* ---------- Lower half ---------- */}
        {/* Lower half should have general items like settings */}
        <div
          style={{ scrollbarWidth: "none" }}
          className={`flex flex-col pt-3 pb-6 gap-4 ${firstMenuShouldCollapse ? "px-2" : "px-4"} border-y border-gray-v2-200`}
        >
          {/* General options, for example: Notification settings & Support */}
          {sidebarV2BottomHalfItems.map((group) => (
            <div key={`sidebar-group-${group.key}`} className="flex flex-col">
              {/* This should be shown but the clowns writing the specs just can't decide themselves. */}
              {/* <span className="px-3 py-1 text-xs font-semibold text-gray-v2-400">
                  {group.getLabel()}
                </span> */}
              {group.products.map((product) => (
                <SidebarProductItemRender
                  key={`sidebar-item-product-${product.path}`}
                  product={product}
                  workspaceId={workspaceId}
                  pathMatches={pathMatches}
                  minified={firstMenuShouldCollapse}
                />
              ))}
            </div>
          ))}
          {/* General options end */}

          {/* User profile */}
          <LayoutV2SidebarUserProfile
            workspaceId={workspaceId}
            minified={firstMenuShouldCollapse}
            active={isOnSettingPage}
          />
          {/* User profile end */}
        </div>
        {/* Lower half end */}
      </div>

      {/* Second menu, will sometimes show in replace of the Main Menu */}
      <div
        className={`py-6 ${secondMenu && !isOnCollapsedMenu ? "w-[calc(100%_-_3.75rem)]" : "w-0 opacity-0"} overflow-hidden`}
      >
        <div
          className={`px-4 flex flex-col gap-4 w-full overflow-y-auto overflow-x-hidden`}
        >
          <Link
            to={generatePath(secondMenu?.backTo.path ?? ".", {
              workspaceId,
            })}
          >
            <button className="flex items-center gap-2 w-fit rounded-lg font-semibold text-md text-brand-v2-700 hover:text-brand-v2-800 whitespace-nowrap">
              <FlipBackward />
              {secondMenu ? secondMenu.backTo.getLabel() : ""}
            </button>
          </Link>
          <span className="text-lg font-semibold text-gray-v2-900 whitespace-nowrap">
            {secondMenu?.getLabel()}
          </span>
          <div className="flex flex-col gap-0.5">
            {secondMenu
              ? secondMenu.children.map((subpage) => (
                  <SidebarPageItemRender
                    key={`sidebar-item-subpage-${subpage.path}`}
                    page={subpage}
                    pathMatches={pathMatches}
                    overwriteClassName="pl-3"
                  />
                ))
              : null}
          </div>
        </div>
      </div>
      {/* Second menu end */}
    </div>
  );
};
